<template>
  <!--
    Native <dialog> + showModal() puts this element in the browser's TOP
    LAYER, which renders above the entire document and escapes every
    containing block, stacking context, transform, filter, overflow, and
    `backdrop-filter` on the page. That's why this fix works regardless of
    where `<v2-modal>` is rendered (including inside `.v2-card--panel`):
    showModal() bypasses ancestor CSS entirely.
    The dialog also gets ESC handling and focus trap for free from the
    platform; we only intercept `cancel` to route through our own close flow.
  -->
  <dialog
    ref="dialog"
    class="v2-modal-overlay"
    :class="{
      'v2-modal-overlay--non-top-layer': !useTopLayer,
      'v2-modal-overlay--body-scroll': bodyScroll,
    }"
    @cancel.prevent="$emit('close')"
    @click.self="$emit('close')"
    @keydown.esc="onEscKey"
  >
    <div
      v-if="show"
      class="v2-modal"
      :class="`v2-modal--${size}`"
      role="dialog"
      aria-modal="true"
    >
      <div class="v2-modal__header">
        <slot name="header">
          <h3 class="v2-modal__title">
            {{ title }}
          </h3>
        </slot>
        <button
          class="v2-modal__close"
          @click="$emit('close')"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line
              x1="18"
              y1="6"
              x2="6"
              y2="18"
            />
            <line
              x1="6"
              y1="6"
              x2="18"
              y2="18"
            />
          </svg>
        </button>
      </div>
      <div
        :id="bodyId || undefined"
        class="v2-modal__body"
      >
        <slot />
      </div>
      <div
        v-if="$slots.footer"
        class="v2-modal__footer"
      >
        <slot name="footer" />
      </div>
    </div>
  </dialog>
</template>

<script>
import { notifyModalOpened, notifyModalClosed } from '@/service/iframe-auth.service'

export default {
  name: 'V2Modal',
  props: {
    show: { type: Boolean, default: false },
    title: { type: String, default: '' },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg', 'xl', '2xl', 'full'].includes(v),
    },
    bodyId: {
      type: String,
      default: '',
    },
    // When true (default), the modal is opened via `showModal()` which puts
    // it in the browser's TOP LAYER — above every z-index, escaping all
    // ancestor stacking/clipping contexts. That's the right choice for
    // most modals, but it has one downside: any popup the modal contains
    // (e.g. a flatpickr calendar that appends to <body>) renders behind
    // the dialog and cannot be lifted in front via z-index, because top
    // layer beats every z-index. Set `use-top-layer="false"` on modals
    // that need to host body-portaled popups (flatpickr, native menus,
    // etc.) — the dialog will use `.show()` instead and a high
    // CSS-driven z-index, so flatpickr's default `z-index: 99999` wins.
    useTopLayer: {
      type: Boolean,
      default: true,
    },
    // When true, the modal CARD is capped to the viewport height and its
    // `__body` scrolls internally, instead of the whole top-layer `<dialog>`
    // scrolling. Use this for TALL modals (e.g. product detail). A scrollable
    // top-layer dialog hits a Chromium repaint bug: content that scrolls in
    // from beyond the initial viewport tile paints transparent, showing the
    // page behind it. Scrolling a normal in-flow element (`__body`) sidesteps
    // that entirely and keeps the header/close pinned. Default false preserves
    // the original overlay-scroll behaviour for every existing modal.
    bodyScroll: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { hasNotifiedOpen: false }
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        // Wait for the dialog ref to exist on first render before calling
        // showModal(). $nextTick after the initial v-bind ensures it.
        this.$nextTick(() => this.syncDialog(val))
      },
    },
  },
  mounted() {
    this.syncDialog(this.show)
  },
  beforeDestroy() {
    const dialog = this.$refs.dialog
    if (dialog && dialog.open) dialog.close()
    document.body.style.overflow = ''
    if (this.hasNotifiedOpen) {
      notifyModalClosed()
      this.hasNotifiedOpen = false
    }
  },
  methods: {
    // Reset the scroll position to the top. Used when the modal content is
    // swapped in place (e.g. navigating recommended products) so the new
    // content starts from the top instead of the prior scroll offset. The
    // scroller is `__body` in body-scroll mode and the dialog otherwise —
    // reset both so it works regardless of mode.
    scrollToTop() {
      const body = this.$el && this.$el.querySelector('.v2-modal__body')
      if (body) body.scrollTop = 0
      if (this.$refs.dialog) this.$refs.dialog.scrollTop = 0
    },
    syncDialog(val) {
      const dialog = this.$refs.dialog
      if (!dialog) return
      if (val) this.openDialog(dialog)
      else this.closeDialog(dialog)
    },
    openDialog(dialog) {
      if (!dialog.open) {
        try {
          // `useTopLayer: false` opt-out — opens the dialog in the regular
          // stacking context instead of the top layer. Callers use this
          // when the modal needs to host body-appended popups (e.g.
          // flatpickr calendars) that would otherwise be trapped behind
          // a top-layer dialog.
          if (this.useTopLayer) dialog.showModal()
          else dialog.show()
        } catch (_) { /* already open */ }
      }
      document.body.style.overflow = 'hidden'
      if (this.hasNotifiedOpen) return
      notifyModalOpened()
      this.hasNotifiedOpen = true
    },
    onEscKey() {
      // `<dialog>.show()` (non-top-layer mode) does NOT fire the `cancel`
      // event on ESC, so the @cancel handler above never runs. Re-add
      // the same close behaviour manually. Inert when in top-layer mode
      // because @cancel handles it and ESC is consumed by the browser
      // before bubbling — this listener is harmless either way.
      if (!this.useTopLayer) this.$emit('close')
    },
    closeDialog(dialog) {
      if (dialog.open) dialog.close()
      document.body.style.overflow = ''
      if (!this.hasNotifiedOpen) return
      notifyModalClosed()
      this.hasNotifiedOpen = false
    },
  },
}
</script>

<style lang="scss" scoped>
// `<dialog>` is `display: none` by default and switches to block when the
// `[open]` attribute is present (set by `showModal()`). We override layout
// here, but only when actually open — so the closed dialog still takes no
// space.
.v2-modal-overlay[open] {
  // showModal() puts this in the top layer, so positioning resolves against
  // the viewport regardless of any ancestor containing block created by
  // backdrop-filter / transform / filter / contain.
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  margin: 0;
  border: 0;
  // Solid translucent dim — intentionally NO backdrop-filter. The PLG parent
  // applies the identical `rgba(0, 0, 0, 0.5)` over its own chrome when this
  // modal opens, so iframe area and PLG chrome composite as one continuous
  // backdrop.
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: var(--v2-space-8) var(--v2-space-4);
  overflow-y: auto;
}

// Body-scroll mode: opt-in via `body-scroll` on the V2Modal. The top-layer
// dialog no longer scrolls (so it can't hit the top-layer repaint bug where
// scrolled-in content paints transparent); instead the card is capped to the
// viewport and `__body` scrolls as a normal in-flow element. The cap subtracts
// the overlay's vertical padding (var(--v2-space-8) top + bottom) so the card
// still sits inside the dimmed margin.
.v2-modal-overlay--body-scroll[open] {
  overflow: hidden;

  .v2-modal {
    max-height: calc(100vh - 2 * var(--v2-space-8));
  }

  .v2-modal__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
}

// Non-top-layer mode: opt-in via `use-top-layer="false"` on the V2Modal.
// `.show()` (instead of `.showModal()`) keeps the dialog in the regular
// stacking context, so body-portaled popups (flatpickr calendar with its
// default `z-index: 99999`) can render above it. We set z-index lower than
// flatpickr's default so the popup wins, but high enough to sit above the
// app chrome. Everything else (size, dim, layout) mirrors top-layer mode.
.v2-modal-overlay--non-top-layer[open] {
  z-index: 9000;
}

// `::backdrop` is the area outside the dialog's box. Because we size the
// dialog to fill the viewport above, ::backdrop is essentially never visible,
// but we style it transparent to be safe (so no double-dim if a future change
// shrinks the dialog box).
.v2-modal-overlay::backdrop {
  background: transparent;
}

// TEMP: glass panel reverted to solid card per user feedback.
// The glass-overlay behind the modal still blurs the page substrate so the
// modal reads as elevated, but the panel itself is opaque white now to
// avoid the cascade of glass-on-glass that made content look murky over
// product images / dense content. Re-enable glass-panel here once we have
// a fix that doesn't muddy nested content (likely needs a higher-opacity
// surface variant or compositional adjustment).
.v2-modal {
  background: var(--v2-card);
  border: 1px solid var(--v2-border);
  border-radius: 1.25rem;
  box-shadow: var(--v2-shadow-lg);
  width: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;

  &--sm  { max-width: 400px; }
  &--md  { max-width: 560px; }
  &--lg  { max-width: 720px; }
  &--xl  { max-width: 960px; }
  &--2xl { max-width: 1200px; }
  &--full { max-width: calc(100vw - var(--v2-space-8)); }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--v2-space-4) var(--v2-space-6);
    border-bottom: 1px solid var(--v2-border);
  }

  &__title {
    font-family: var(--v2-font-display);
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin: 0;
  }

  &__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    background: transparent;
    border: none;
    border-radius: var(--v2-radius-sm);
    color: var(--v2-muted-foreground);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: var(--v2-muted);
      color: var(--v2-foreground);
    }
  }

  &__body {
    padding: var(--v2-space-6);
    flex: 1;
  }

  &__footer {
    padding: var(--v2-space-4) var(--v2-space-6);
    border-top: 1px solid var(--v2-border);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--v2-space-2);
  }
}

@media (prefers-reduced-transparency: reduce) {
  .v2-modal-overlay[open] {
    // When transparency is disabled, fall back to a solid dim so the modal
    // reads as elevated. Without backdrop-filter the substrate stays sharp,
    // so a heavier dim is acceptable and necessary for visual separation.
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: none;
  }
  // .v2-modal already opaque (TEMP revert), so no override needed here.
}

.v2-modal-fade-enter-active,
.v2-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.v2-modal-fade-enter,
.v2-modal-fade-leave-to {
  opacity: 0;
}
</style>

<!--
  Global override (NOT scoped). Inside any v2-modal__body — i.e. inside any
  V2Modal — everything drops its glassmorphic styling and renders with the
  legacy solid surfaces. Two reasons:

  1. Performance — modals (especially Add to Order / Add Products with 50+
     products) suffer from per-card backdrop-filter compositing pressure
     during fast scroll. Removing blur on the modal subtree restores
     pre-migration scroll smoothness.
  2. Visual — V2Modal already provides its own opaque panel; glass-on-glass
     reads muddy and the modal's own dim/blur is what should dominate.

  The technique:
  - Locally re-map the glass design tokens (--v2-glass-surface-?, --v2-glass-
    stroke, --v2-glass-shadow) to legacy values *inside* .v2-modal__body.
    Any component descendant that uses `var(--v2-glass-*)` automatically
    picks up the remapped legacy values without needing a per-component
    override.
  - Force `backdrop-filter: none` on every descendant element to kill any
    inline / hard-coded blur (e.g. components that don't use the tokens).

  Outside V2Modal, glass design is untouched — every page surface keeps
  its frosted-glass look.
-->
<style lang="scss">
.v2-modal__body {
  // Re-map glass tokens to legacy equivalents — every descendant that uses
  // these custom properties via `var(...)` resolves to the legacy value.
  --v2-glass-surface-1: var(--v2-card);
  --v2-glass-surface-2: var(--v2-muted);
  --v2-glass-surface-floating: var(--v2-card);
  --v2-glass-stroke: var(--v2-border);
  --v2-glass-stroke-strong: var(--v2-border);
  --v2-glass-shadow: var(--v2-shadow-sm);
  --v2-glass-shadow-floating: var(--v2-shadow-md);
  --v2-glass-sheen: none;

  // Kill any backdrop-filter applied directly on descendants. `!important`
  // overrides scoped declarations inside child components.
  *,
  *::before,
  *::after {
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }
}
</style>
